import React, { useState } from 'react';
import YouTubeExtractor from './components/YouTubeExtractor';
import BaseThumbnailGenerator from './components/BaseThumbnailGenerator';
import FaceUploader from './components/FaceUploader';
import CanvasEditor from './components/CanvasEditor';
import './App.css';

function App() {
  const [thumbnailData, setThumbnailData] = useState(null);
  const [baseThumbnail, setBaseThumbnail] = useState(null);
  const [faceCutouts, setFaceCutouts] = useState([]);
  const [currentStep, setCurrentStep] = useState(1);

  const handleThumbnailExtracted = (data) => {
    setThumbnailData(data);
    setCurrentStep(2);
  };

  const handleBaseThumbnailGenerated = (data) => {
    setBaseThumbnail(data);
    setCurrentStep(3);
  };

  const handleFaceCutoutsReady = (cutouts) => {
    setFaceCutouts(cutouts);
    if (baseThumbnail && cutouts.length > 0) {
      setCurrentStep(4);
    }
  };

  const resetWorkflow = () => {
    setThumbnailData(null);
    setBaseThumbnail(null);
    setFaceCutouts([]);
    setCurrentStep(1);
  };

  const getStepStatus = (step) => {
    if (step < currentStep) return 'completed';
    if (step === currentStep) return 'active';
    return 'pending';
  };

  return (
    <div className="App">
      <header className="app-header">
        <h1>🎙️ AI Thumbnail Generator</h1>
        <p>Create professional podcast thumbnails with AI-powered background removal and custom face overlays</p>
        
        <div className="workflow-progress">
          <div className={`step-indicator ${getStepStatus(1)}`}>
            <span className="step-number">1</span>
            <span className="step-label">Extract YouTube Thumbnail</span>
          </div>
          
          <div className={`step-indicator ${getStepStatus(2)}`}>
            <span className="step-number">2</span>
            <span className="step-label">Generate AI Base</span>
          </div>
          
          <div className={`step-indicator ${getStepStatus(3)}`}>
            <span className="step-number">3</span>
            <span className="step-label">Upload Guest Faces</span>
          </div>
          
          <div className={`step-indicator ${getStepStatus(4)}`}>
            <span className="step-number">4</span>
            <span className="step-label">Create Final Thumbnail</span>
          </div>
        </div>
        
        <button onClick={resetWorkflow} className="reset-btn">
          🔄 Start Over
        </button>
      </header>

      <main className="app-main">
        <div className="workflow-container">
          <YouTubeExtractor onThumbnailExtracted={handleThumbnailExtracted} />
          
          <BaseThumbnailGenerator 
            thumbnailData={thumbnailData}
            onBaseThumbnailGenerated={handleBaseThumbnailGenerated}
          />
          
          <FaceUploader onFaceCutoutsReady={handleFaceCutoutsReady} />
          
          <CanvasEditor 
            baseThumbnail={baseThumbnail}
            faceCutouts={faceCutouts}
          />
        </div>
      </main>

      <footer className="app-footer">
        <div className="footer-content">
          <div className="feature-list">
            <h3>Features</h3>
            <ul>
              <li>✨ AI-powered background removal</li>
              <li>🎨 Custom title text overlay</li>
              <li>👥 Multiple guest face cutouts</li>
              <li>🖱️ Drag & drop canvas editing</li>
              <li>📥 High-quality PNG download</li>
            </ul>
          </div>
          
          <div className="tech-stack">
            <h3>Powered By</h3>
            <ul>
              <li>React + Fabric.js</li>
              <li>Replicate API</li>
              <li>Flux-Kontext AI</li>
              <li>rembg Background Removal</li>
            </ul>
          </div>
          
          <div className="workflow-info">
            <h3>How It Works</h3>
            <ol>
              <li>Extract thumbnail from YouTube video</li>
              <li>AI removes people & adds custom title</li>
              <li>Upload guest photos for face cutouts</li>
              <li>Compose final thumbnail on interactive canvas</li>
            </ol>
          </div>
        </div>
        
        <div className="footer-bottom">
          <p>&copy; 2024 AI Thumbnail Generator. Powered by advanced AI models.</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
